import type {
	FetchWrapperRequestOptions,
	FetchWrapperResponse,
	HandleResponseOrigRequestParams
} from './fetchWrapper.types.ts';

import { useAuthStore } from '@/stores';
import { APIHttpError } from './error/apiHttpError';

export const baseUrl = `${import.meta.env.VITE_API_URL}`;
export const refreshTokenUrl = `${baseUrl}/session/heartbeat`;

const credentials = `${import.meta.env.VITE_FETCH_CREDENTIALS}` as RequestCredentials;

export const fetchWrapper = {
	get: request('GET'),
	getBlob: request('GETBLOB'),
	patch: request('PATCH'),
	post: request('POST'),
	put: request('PUT'),
	delete: request('DELETE')
};

function request(method: string) {
	return (
		url: string,
		body?: BodyInit | null,
		{ credentials: credentialsOverride }: FetchWrapperRequestOptions = {}
	) => {
		const requestOptions: RequestInit = {
			credentials,
			method: method === 'GETBLOB' ? 'GET' : method,
			mode: 'cors',
			cache: 'no-cache',
			headers: authHeader(url)
		};
		if (body && ['POST', 'PUT', 'PATCH'].includes(method)) {
			(requestOptions.headers as Headers).set('Content-Type', 'application/json');
			requestOptions.body = JSON.stringify(body);
		}
		if (credentialsOverride) {
			requestOptions.credentials = credentialsOverride;
		}
		return fetch(url, requestOptions).then((response) =>
			handleResponse(response, {
				url,
				requestOptions,
				isBlob: method === 'GETBLOB'
			})
		);
	};
}

// helper functions

function authHeader(url: string): Headers {
	// return auth header with jwt if user is logged in and request is to the api url
	const { authToken } = useAuthStore();
	const isApiUrl = url.startsWith(import.meta.env.VITE_API_URL);
	if (authToken && isApiUrl) {
		return new Headers({ Authorization: `Bearer ${authToken}` });
	} else {
		return new Headers({});
	}
}

function handleResponse(
	response: Response,
	origRequestParams: HandleResponseOrigRequestParams
): Promise<FetchWrapperResponse> {
	const errorHandler = (errResponse: Response) => {
		return Promise.reject(new APIHttpError(errResponse.statusText, errResponse));
	};
	const { url, requestOptions, isBlob } = origRequestParams;
	const { body, method } = requestOptions;
	if (!response.ok) {
		const { authToken, logout, refreshToken } = useAuthStore();
		if (response.status == 401 && authToken) {
			if (!(method === 'GET' && url === refreshTokenUrl)) {
				return refreshToken()
					.then((result) => {
						if (result) {
							switch ((isBlob && 'GETBLOB') || method) {
								case 'GETBLOB':
									return fetchWrapper.getBlob(url);
								case 'GET':
									return fetchWrapper.get(url);
								case 'PATCH':
									return fetchWrapper.patch(url, body);
								case 'POST':
									return fetchWrapper.post(url, body);
								case 'PUT':
									return fetchWrapper.put(url, body);
								case 'DELETE':
									return fetchWrapper.delete(url);
								default:
									throw new Error('Unknown method');
							}
						}
						throw new Error('Refresh token not received.');
					})
					.catch(() => errorHandler(response));
			}
		} else if (response.status == 403 && authToken) {
			// auto logout if 403 Forbidden response returned from api
			logout();
		}
		return errorHandler(response);
	}
	if (isBlob) {
		return Promise.resolve({ response });
	}
	return response.text().then((text) => {
		let data = null;
		try {
			data = text && JSON.parse(text);
		} catch (e) {
			data = null;
		}
		const retVal: FetchWrapperResponse = { response };
		if (data) {
			retVal.data = data;
		}
		return Promise.resolve(retVal);
	});
}
